import { Avatar, Skeleton } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import {
  capitalize,
  getDataWithFilterUrl,
} from "../../controllers/APIController";
import { Colors } from "../../utils/constant";
import { formatDate } from "../../utils/helper";

/**
 * Sample log data structure for reference:
 * [
 *   {
 *     "by": 7,
 *     "operation": "update",
 *     "timestamp": 1727068659180
 *   },
 *   {
 *     "by": 7,
 *     "operation": "create",
 *     "timestamp": 1727068438419
 *   }
 * ]
 * 
 * User data includes ProfilePictureUrl, FirstName, email, and id
 */

/**
 * LogSkeleton Component
 * Loading skeleton for log entries while user data is being fetched
 * @returns {JSX.Element} Skeleton loading component
 */
const LogSkeleton = () => {
  return (
    <div className="w-full flex items-center gap-3 my-2">
      <div>
        <Skeleton className="flex rounded-full w-8 h-8" />
      </div>
      <div className="w-full flex flex-col gap-2">
        <Skeleton className="h-3 w-full rounded-lg" />
      </div>
    </div>
  );
};

/**
 * LogsInfo Component
 * Displays a scrollable list of log entries showing user actions on metric responses
 * Fetches user information for each log entry and displays with avatars and timestamps
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of log entries with user ID, operation, and timestamp
 * @returns {JSX.Element|null} Log entries list or null if no data
 */
const LogsInfo = ({ data }) => {
  // Loading and user data states
  const [isLoading, setIsLoading] = useState(true);
  const [userDataList, setUserDataList] = useState({});

  /**
   * Fetch user information for all log entries
   * Updates user data state when complete
   */
  useEffect(() => {
    const userInfo = {};
    
    if (!data) {
      setUserDataList({});
      setIsLoading(false);
      return;
    }

    const fetchUserData = data?.map(async (item) => {
      const response = await getDataWithFilterUrl(
        "users",
        `filters[id][$eqi]=${item?.by}`
      );
      
      userInfo[item.by] = {
        id: response?.[0]?.id,
        email: response?.[0]?.email || "",
        ProfilePictureUrl: response?.[0]?.ProfilePictureUrl || "",
        FirstName: response?.[0]?.FirstName,
      };
      
      return response;
    });

    Promise.all(fetchUserData)
      .then((data) => {
        setUserDataList(userInfo);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
        setIsLoading(false);
      });
  }, [data]);

  // Return null if no log data
  if (!data?.length || data?.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col h-96 overflow-scroll custom-scrollbar">
      {isLoading ? (
        // Show loading skeletons while fetching user data
        <>
          <LogSkeleton />
          <LogSkeleton />
          <LogSkeleton />
        </>
      ) : (
        // Render log entries with user information
        data?.map((item, index) => {
          const currentUserInfo = userDataList?.[item?.by];
          
          // Generate display name from first name or email
          const name = currentUserInfo?.FirstName
            ? currentUserInfo?.FirstName
            : capitalize(currentUserInfo?.email?.split("@")[0] || "Unknown");
          
          // Format timestamp for display
          const dateValue = formatDate(item?.timestamp);
          
          // Generate avatar color based on user ID
          const numColors = Colors.length;
          const colorIndex = currentUserInfo && currentUserInfo.id % numColors;
          
          return (
            <div key={`${item?.by}-${item?.timestamp}-${index}`} className="flex items-center my-2 gap-2">
              {/* User Avatar */}
              <Avatar
                radius="md"
                size="sm"
                src={
                  currentUserInfo?.ProfilePictureUrl &&
                  currentUserInfo?.ProfilePictureUrl
                }
                color={Colors[colorIndex]}
                name={
                  currentUserInfo && currentUserInfo?.FirstName !== null
                    ? currentUserInfo?.FirstName?.slice(0, 1).toUpperCase()
                    : currentUserInfo?.email?.slice(0, 1).toUpperCase()
                }
              />
              
              {/* Log Entry Text */}
              <p className="flex items-center gap-2 w-full text-sm">
                <span className="font-medium">{name}</span>
                <span>{item?.operation} the response</span>
                <span className="text-gray-500">{dateValue}</span>
              </p>
            </div>
          );
        })
      )}
    </div>
  );
};

export default LogsInfo;
